import { TrophyContainer, LayerImg } from './styled-components';

// ── Full trophy SVGs per level ──────────────────────────────────────────────
import beginnerSvg from '@/shared/icons/Gamification/beginner.svg';
import bronzeSvg from '@/shared/icons/Gamification/bronze.svg';
import silverSvg from '@/shared/icons/Gamification/silver.svg';
import goldSvg from '@/shared/icons/Gamification/gold.svg';
import platiniumSvg from '@/shared/icons/Gamification/platinium.svg';
import diamondSvg from '@/shared/icons/Gamification/diamond.svg';
import eliteSvg from '@/shared/icons/Gamification/elite.svg';

// ── Disabled trophy SVGs ────────────────────────────────────────────────────
import beginnerDisabledSvg from '@/shared/icons/Gamification/beginner-disabled.svg';
import bronzeDisabledSvg from '@/shared/icons/Gamification/bronze-disabled.svg';
import silverDisabledSvg from '@/shared/icons/Gamification/silver-disabled.svg';
import goldDisabledSvg from '@/shared/icons/Gamification/gold-disabled.svg';
import platiniumDisabledSvg from '@/shared/icons/Gamification/platinium-disabled.svg';
import diamondDisabledSvg from '@/shared/icons/Gamification/diamond-disabled.svg';
import eliteDisabledSvg from '@/shared/icons/Gamification/elite-disabled.svg';

// ── Banner SVGs ─────────────────────────────────────────────────────────────
import bannerDefaultSvg from '@/shared/icons/Gamification/banner-default.svg';
import bannerCompletedSvg from '@/shared/icons/Gamification/banner-completed.svg';
import bannerDisabledSvg from '@/shared/icons/Gamification/banner-disabled.svg';

// ── Overlay icons ───────────────────────────────────────────────────────────
import IconHouse from '@/shared/icons/Gamification/IconHouse';
import IconComputer from '@/shared/icons/Gamification/IconComputer';
import IconArticle from '@/shared/icons/Gamification/IconArticle';
import IconCycle from '@/shared/icons/Gamification/IconCycle';
import IconMail from '@/shared/icons/Gamification/IconMail';
import IconMobile from '@/shared/icons/Gamification/IconMobile';
import IconTarget from '@/shared/icons/Gamification/IconTarget';
import IconPsychiatry from '@/shared/icons/Gamification/IconPsychiatry';
import IconCheck from '@/shared/icons/Gamification/IconCheck';

const LEVEL_SVGS = {
  beginner: beginnerSvg,
  bronze: bronzeSvg,
  silver: silverSvg,
  gold: goldSvg,
  platinium: platiniumSvg,
  diamond: diamondSvg,
  elite: eliteSvg,
};

const LEVEL_DISABLED_SVGS = {
  beginner: beginnerDisabledSvg,
  bronze: bronzeDisabledSvg,
  silver: silverDisabledSvg,
  gold: goldDisabledSvg,
  platinium: platiniumDisabledSvg,
  diamond: diamondDisabledSvg,
  elite: eliteDisabledSvg,
};

const BANNER_SVGS = {
  default: bannerDefaultSvg,
  completed: bannerCompletedSvg,
  disabled: bannerDisabledSvg,
};

const BANNER_TEXT_COLORS = {
  default: '#5C6575',
  completed: '#343535',
  disabled: '#939597',
};

const CATEGORY_OVERLAY = {
  contract:    { width: 32, height: 32, icon: IconTarget },
  axa:         { width: 32, height: 32, icon: IconHouse },
  offset:      { width: 32, height: 32, icon: IconPsychiatry },
  lead:        { width: 32, height: 32, icon: IconCycle },
  protected:   { width: 32, height: 32, icon: IconComputer },
  neat:        { width: 32, height: 32, icon: IconArticle },
  nlbp:        { width: 32, height: 32, icon: IconMail },
  myselectra:  { width: 24, height: 24, icon: IconMobile },
};

// SVG natural dimensions & hex centre for overlay positioning
const SVG_W = 110;
const SVG_H = 103;
const HEX_CX = 55;
const HEX_CLIP_Y = 75;
const OVERLAY_SIZE = 24;

// Banner SVG dimensions
const BANNER_SVG_W = 94;
const BANNER_SVG_H = 24;
const BANNER_GAP = -2;

const TrophyBadge = ({
  categoryId,
  trophyColor = 'empty',
  width = 90,
  dimmed = false,
  numberOverlay = null,
  bannerLabel = null,
  bannerVariant = 'default',
}) => {
  const disabledSvg = LEVEL_DISABLED_SVGS[trophyColor];
  const svgSrc = dimmed && disabledSvg ? disabledSvg : LEVEL_SVGS[trophyColor];
  const needsFilter = (dimmed && !disabledSvg) || !LEVEL_SVGS[trophyColor];
  const OverlayIcon = CATEGORY_OVERLAY[categoryId]?.icon || null;
  const overlaySizeW = CATEGORY_OVERLAY[categoryId]?.width || OVERLAY_SIZE;
  const overlaySizeH = CATEGORY_OVERLAY[categoryId]?.height || OVERLAY_SIZE;

  const scale = width / SVG_W;
  const renderedH = SVG_H * scale;

  const showSeparateBanner = bannerLabel !== null;
  const hexClipHeight = HEX_CLIP_Y * scale;
  const bannerWidth = width;
  const bannerHeight = (BANNER_SVG_H / BANNER_SVG_W) * bannerWidth;
  const bannerLeft = 0;
  const totalHeight = showSeparateBanner
    ? hexClipHeight + BANNER_GAP + bannerHeight
    : renderedH;

  return (
    <TrophyContainer $width={width} $height={totalHeight}>
      <LayerImg
        src={svgSrc || beginnerSvg}
        alt=""
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${width / 16}rem`,
          height: `${renderedH / 16}rem`,
          ...(needsFilter ? { filter: 'grayscale(1) opacity(0.4)' } : {}),
          ...(showSeparateBanner ? {
            clipPath: `inset(0 0 ${((SVG_H - HEX_CLIP_Y) / SVG_H) * 100}% 0)`,
          } : {}),
        }}
      />
      { numberOverlay !== null && (
        <div style={{
          position: 'absolute',
          left: `${(HEX_CX * scale) / 16}rem`,
          top: `${(44 * scale) / 16}rem`,
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '"Inter", sans-serif',
          fontSize: `${(24 * scale) / 16}rem`,
          fontWeight: 700,
          lineHeight: 1,
          color: dimmed ? '#B0B3B8' : '#343535',
          pointerEvents: 'none',
        }}>
          {numberOverlay === 'check' ? (
            <IconCheck style={{ width: `${(28 * scale) / 16}rem`, height: `${(28 * scale) / 16}rem` }} />
          ) : numberOverlay}
        </div>
       )
      }
      {OverlayIcon && (
        <OverlayIcon
          alt=""
          style={{
            position: 'absolute',
            left: `${((HEX_CX - overlaySizeW / 2) * scale) / 16}rem`,
            top: `${((44 - overlaySizeH / 2) * scale) / 16}rem`,
            width: `${(overlaySizeW * scale) / 16}rem`,
            height: `${(overlaySizeH * scale) / 16}rem`,
            pointerEvents: 'none',
          }}
        />
      )}
      {showSeparateBanner && (
        <div style={{
          position: 'absolute',
          left: `${bannerLeft / 16}rem`,
          top: `${(hexClipHeight + BANNER_GAP) / 16}rem`,
          width: `${bannerWidth / 16}rem`,
          height: `${bannerHeight / 16}rem`,
        }}>
          <img
            src={BANNER_SVGS[bannerVariant] || bannerDefaultSvg}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
          <span style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: '18%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Inter", sans-serif',
            fontSize: `${(12 * scale) / 16}rem`,
            fontWeight: 800,
            color: BANNER_TEXT_COLORS[bannerVariant] || '#5C6575',
            pointerEvents: 'none',
          }}>
            {bannerLabel}
          </span>
        </div>
      )}
    </TrophyContainer>
  );
};

export default TrophyBadge;
